import { mergeWith } from 'lodash';
import type { State, HelpCenterState } from './types';
import { getLocale } from './user';

/**
 * Help center selectors for common state operations.
 * These selectors are designed to avoid cyclic dependencies.
 */

export const getHelpCenterState = (state: State): HelpCenterState =>
	mergeWith(
		{} as HelpCenterState,
		state.persisted.helpCenter,
		state.ui?.helpCenterPreview,
		// If the value is undefined we will instead change it to null, the reason for this is to ensure that the resulting
		// property gets replaced with null, by default mergeWith (and merge) does not replace properties if the value is undefined
		(_: unknown, srcValue: unknown) => {
			if (srcValue === undefined) {
				return null;
			}
			// If the value is not specifically 'undefined' we leave it up to the default customizer to handle
			return undefined;
		},
	);

export const getHelpCenterBanner = (state: State): string | undefined => {
	return getHelpCenterState(state).helpCenterBannerUrl;
};

export const getThemeColor = (state: State): string => {
	return getHelpCenterState(state).themeColor;
};

const getTranslation = (state: State, language: string) =>
	getHelpCenterState(state).translations[language];

export const getSharedPortalName = (state: State) => {
	const userLocale = getLocale(state);
	const translation = userLocale && getTranslation(state, userLocale);

	if (translation && translation.sharedPortalName) {
		return translation.sharedPortalName;
	}

	return getHelpCenterState(state).sharedPortalName;
};

export const getCanCustomize = (state: State): boolean => {
	return getHelpCenterState(state).canCustomizeHelpCenter;
};
